/**
 * Contract between the app shell (screens, networking, voice, settings) and the
 * game engine (simulation loop, rendering, input, audio).
 *
 * The shell renders <GameScreen {...props} /> while its room is in the countdown /
 * race phase, both for racers and spectators. Everything under src/game, src/render,
 * src/input and src/audio belongs to the engine; the shell only provides these props.
 * Keep this file small and stable — both sides depend on it.
 */
import type {
  LevelData,
  PlayerId,
  PlayerNetState,
  PlayerProfile,
  RaceEvent,
  RaceProgressEntry,
  RoomMember,
} from '@cube-rush/shared';
import type { ReadonlySignal } from '@preact/signals';
import type { Season } from '../season/season';

export interface ActiveRace {
  raceId: string;
  /** Against the clock: more levels than anybody can complete before the time is up. */
  levels: readonly LevelData[];
  /** Race start on the synchronized server clock (ms); compare with GameNetBridge.serverNow(). */
  startAt: number;
  /**
   * null: a race by levels (first over the last finish line wins). Otherwise a race against
   * the clock: it ends at this server time for everybody, and the levels completed by then are
   * the score.
   */
  endAt: number | null;
  participants: readonly PlayerId[];
  /** Level to (re)start from: 0 for a fresh race, later after a reconnect or page reload. */
  resumeLevel: number;
}

export interface GameNetBridge {
  /** Synchronized server clock in ms (like Date.now(), corrected with ping/pong). */
  serverNow(): number;
  /**
   * Throttling is the engine's job (STATE_SEND_HZ). Silently dropped while offline or while the
   * connection is backed up (a newer position follows).
   */
  sendState(state: PlayerNetState): void;
  /** Queued and delivered after a reconnect if the connection is down. */
  sendEvent(event: RaceEvent): void;
  /**
   * Positions of the other racers: as they reach the server, or batched (at most 20 batches a
   * second). Returns an unsubscribe function.
   */
  onStates(
    listener: (players: ReadonlyArray<{ id: PlayerId; s: PlayerNetState }>) => void,
  ): () => void;
  /** Events of the other racers (deaths, boosts, finished levels). Returns an unsubscribe function. */
  onEvent(listener: (from: PlayerId, event: RaceEvent) => void): () => void;
}

export interface GameVoiceBridge {
  muted: ReadonlySignal<boolean>;
  /** False when the microphone is unavailable or permission was denied. */
  micAvailable: ReadonlySignal<boolean>;
  /**
   * Voice is on, but the microphone went away and was not refused (a call, the app in the
   * background): a tap on retryMic() may bring it back.
   */
  micLost: ReadonlySignal<boolean>;
  toggleMute(): void;
  /** Asks for the microphone again; call it inside the tap (iOS wants a gesture). */
  retryMic(): void;
  /** Ids of the players who are talking right now (including the local player). */
  speaking: ReadonlySignal<ReadonlySet<PlayerId>>;
}

/** auto = split screen on big screens (tablet / computer), one view with ghosts on phones. */
export type LayoutPreference = 'auto' | 'single' | 'split';

export interface GameSettings {
  /** 0..1 */
  sfxVolume: number;
  /** 0..1 */
  musicVolume: number;
  layout: LayoutPreference;
  /** Show other racers on the same level as translucent "shadows". */
  showGhosts: boolean;
  /** Vibrate on death: the phone, or the gamepad played with (where supported). */
  haptics: boolean;
  /**
   * Holiday decorations of the levels and holiday tunes ('none': the plain game). Resolved by
   * the shell from the date, the «Праздничное оформление» setting and the dev override
   * (src/season/season.ts). Never touches physics, levels or timing.
   */
  season: Season;
}

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  sfxVolume: 0.8,
  musicVolume: 0.5,
  layout: 'auto',
  showGhosts: true,
  haptics: true,
  season: 'none',
};

export interface GameScreenProps {
  race: ActiveRace;
  me: { id: PlayerId; profile: PlayerProfile };
  /** True when the local player is not racing (joined mid-race or gave up). */
  spectator: boolean;
  members: ReadonlySignal<readonly RoomMember[]>;
  progress: ReadonlySignal<readonly RaceProgressEntry[]>;
  settings: ReadonlySignal<GameSettings>;
  net: GameNetBridge;
  voice: GameVoiceBridge;
  /** The player wants to leave the race; the shell sends `race.quit` and updates the UI. */
  onQuit(): void;
  /** Opens the shell's settings dialog (volumes, layout, ...). */
  onOpenSettings?(): void;
  /**
   * While the game is on screen, Android's Back calls `handler` instead of leaving the app
   * (the game opens or closes its menu). Returns the function that ends this.
   */
  registerBack?(handler: () => void): () => void;
}
